import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronDown, ShieldCheck } from "lucide-react";
import { useGet } from "@/lib/api";
import { fmt } from "@/lib/meta";

const TONE = { extreme: "bg-[#7A1F2B]", severe: "bg-terracotta", moderate: "bg-[#8C5E0A]", minor: "bg-[#8C5E0A]" };
const LEVEL = { extreme: "Stufe 4", severe: "Stufe 3", moderate: "Stufe 2", minor: "Stufe 1" };

const AlertItem = ({ a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`${TONE[a.severity] || TONE.minor} text-white`} data-testid={`alert-${a.id === "surge" ? "surge" : "dwd"}`}>
      <button onClick={() => setOpen(!open)} className="flex w-full items-start gap-3 px-5 py-3 text-left md:px-6" data-testid="alert-toggle">
        <AlertTriangle size={18} className="mt-0.5 shrink-0" />
        <span className="flex-1">
          <span className="font-sub text-sm font-medium">{a.event}</span>
          <span className="ml-2 text-xs text-white/95">{LEVEL[a.severity]} · {a.towns.join(", ")}</span>
          <span className="block text-xs text-white/95">
            {a.onset && `ab ${fmt(a.onset.slice(0, 16), "EEE HH:mm")}`}{a.expires && ` bis ${fmt(a.expires.slice(0, 16), "EEE HH:mm")}`}
          </span>
        </span>
        <ChevronDown size={16} className={`mt-1 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-white/20 px-5 py-3 text-sm leading-relaxed md:px-6" data-testid="alert-details">
          <p>{a.description}</p>
          {a.instruction && <p className="mt-2 whitespace-pre-line text-white/85">{a.instruction}</p>}
        </div>
      )}
    </div>
  );
};

export const WeatherAlerts = ({ compact = false }) => {
  const { data, isError } = useGet("coast-alerts", "/coast/alerts", undefined, { refetchInterval: 600_000 });
  if (isError && !compact) return <p className="border border-foreground/15 bg-white/50 px-5 py-3 text-sm text-muted-foreground" data-testid="alerts-unavailable">Warnungen sind gerade nicht abrufbar – bitte direkt beim Deutschen Wetterdienst prüfen.</p>;
  if (!data) return null;
  if (!data.length) {
    return compact ? null : (
      <p className="flex items-center gap-2 border border-foreground/15 bg-white/50 px-5 py-3 text-sm" data-testid="alerts-none">
        <ShieldCheck size={16} className="text-[#3F5E45]" /> Aktuell keine amtlichen Unwetter- oder Sturmflutwarnungen für Ostfriesland.
      </p>
    );
  }
  const list = compact ? data.slice(0, 1) : data;
  return (
    <div className="space-y-px" data-testid="weather-alerts">
      {list.map((a) => <AlertItem key={a.id} a={a} />)}
      {compact && (
        <Link to="/wetter" className="block bg-ocean px-5 py-2 font-sub text-xs text-shell hover:bg-ocean/90 md:px-6" data-testid="alerts-more-link">
          {data.length > 1 ? `+ ${data.length - 1} weitere Warnung${data.length > 2 ? "en" : ""} · ` : ""}Alle Details auf Wetter & Gezeiten
        </Link>
      )}
      <p className="pt-1 text-[0.65rem] text-muted-foreground">Quelle: Deutscher Wetterdienst (via Bright Sky) · Pegel: WSV Pegelonline</p>
    </div>
  );
};
